import { z } from "zod";
import {
  FIELD,
  getFieldsForProject,
  projectSupportsBilling,
} from "../config/companyFields";

const THAILAND_COUNTRY_ID = "204";

const VALIDATORS = {
  [FIELD.NAME_TH]: z
    .string()
    .trim()
    .min(1, "กรุณากรอกชื่อบริษัท")
    .max(100, "ชื่อบริษัทยาวเกินไป"),

  [FIELD.NAME_EN]: z
    .string()
    .trim()
    .max(100, "ชื่อบริษัทยาวเกินไป")
    .optional()
    .or(z.literal("")),

  [FIELD.EMAIL]: z
    .string()
    .trim()
    .min(1, "กรุณากรอกอีเมล")
    .email("รูปแบบอีเมลไม่ถูกต้อง")
    .max(100, "อีเมลยาวเกินไป"),

  [FIELD.PHONE]: z
    .string()
    .trim()
    .min(1, "กรุณากรอกเบอร์โทร")
    .regex(/^[\d\s\-+()]+$/, "เบอร์โทรมีอักขระไม่ถูกต้อง")
    .min(9, "เบอร์โทรสั้นเกินไป")
    .max(20, "เบอร์โทรยาวเกินไป"),

  [FIELD.CONTACT_PERSON]: z
    .string()
    .trim()
    .max(100)
    .optional()
    .or(z.literal("")),

  [FIELD.ADDRESS]: z
    .string()
    .trim()
    .max(500, "ที่อยู่ยาวเกินไป")
    .optional()
    .or(z.literal("")),

  [FIELD.NUM_ORGANIZATION]: z
    .union([
      z.literal(""),
      z.string().regex(/^\d+$/, "ต้องเป็นตัวเลขเท่านั้น"),
      z.number().int().nonnegative(),
    ])
    .optional(),
};

// ─── Billing section validators ────────────────────────────
//
// All loose at field-level — exact rules enforced via cross-field refines below
// (since most rules depend on detail_type or id_country).

const BILLING_SHAPE = {
  detail_type: z.enum(["company", "personal"], {
    errorMap: () => ({ message: "กรุณาเลือกประเภทผู้สมัคร" }),
  }),

  tax_number: z.string().trim().min(1, "กรุณากรอกเลข"),

  // UI-only toggle — controls name_branch visibility
  is_branch_open: z.boolean().default(false),

  name_branch: z
    .union([
      z.literal(""),
      z.string().regex(/^\d{5}$/, "เลขที่สาขาต้องเป็นตัวเลข 5 หลัก"),
    ])
    .optional(),

  // ─── Address group ───
  address2: z
    .string()
    .trim()
    .max(255, "ยาวเกินไป")
    .optional()
    .or(z.literal("")),

  id_country: z
    .string()
    .trim()
    .min(1, "กรุณาเลือกประเทศ")
    .max(20),

  // Required only when country is Thailand — enforced via refine
  id_province: z.string().trim().max(20).optional().or(z.literal("")),
  id_district: z.string().trim().max(20).optional().or(z.literal("")),
  id_subdistrict: z.string().trim().max(20).optional().or(z.literal("")),
  postal_code: z
    .string()
    .trim()
    .max(10, "รหัสไปรษณีย์ยาวเกินไป")
    .optional()
    .or(z.literal("")),
};

const isFilled = (v) => typeof v === "string" && v.length > 0;

export const createCompanySchema = (projectId) => {
  const fields = getFieldsForProject(projectId);
  const includeBilling = projectSupportsBilling(projectId);

  const shape = fields.reduce((acc, fieldId) => {
    const validator = VALIDATORS[fieldId];
    if (validator) acc[fieldId] = validator;
    return acc;
  }, {});

  if (includeBilling) {
    Object.assign(shape, BILLING_SHAPE);
  }

  let schema = z.object(shape);

  if (includeBilling) {
    schema = schema
      // ─── 1. tax_number format depends on detail_type ───
      .refine(
        (data) => {
          if (data.detail_type === "company") {
            return /^\d{13}$/.test(data.tax_number);
          }
          return (
            /^\d{13}$/.test(data.tax_number) ||
            /^[A-Z0-9]{6,15}$/i.test(data.tax_number)
          );
        },
        (data) => ({
          message:
            data.detail_type === "company"
              ? "เลขผู้เสียภาษีต้องมี 13 หลัก"
              : "เลขบัตรประชาชน 13 หลัก หรือ Passport 6-15 ตัวอักษร",
          path: ["tax_number"],
        })
      )
      // ─── 2. branch number required when toggle is open ───
      .refine(
        (data) =>
          !data.is_branch_open ||
          (typeof data.name_branch === "string" && data.name_branch.length > 0),
        {
          message: "กรุณากรอกเลขที่สาขา",
          path: ["name_branch"],
        }
      )
      // ─── 3. personal can't have branch ───
      .refine(
        (data) => !(data.detail_type === "personal" && data.is_branch_open),
        {
          message: "บุคคลธรรมดาไม่สามารถระบุสาขาได้",
          path: ["is_branch_open"],
        }
      )
      // ─── 4. Thailand → all 4 address fields required ───
      .refine(
        (data) =>
          data.id_country !== THAILAND_COUNTRY_ID || isFilled(data.id_province),
        { message: "กรุณาเลือกจังหวัด", path: ["id_province"] }
      )
      .refine(
        (data) =>
          data.id_country !== THAILAND_COUNTRY_ID || isFilled(data.id_district),
        { message: "กรุณาเลือกอำเภอ/เขต", path: ["id_district"] }
      )
      .refine(
        (data) =>
          data.id_country !== THAILAND_COUNTRY_ID ||
          isFilled(data.id_subdistrict),
        { message: "กรุณาเลือกตำบล/แขวง", path: ["id_subdistrict"] }
      )
      .refine(
        (data) =>
          data.id_country !== THAILAND_COUNTRY_ID || isFilled(data.postal_code),
        { message: "กรุณาเลือกตำบลเพื่อเติมรหัสไปรษณีย์", path: ["postal_code"] }
      );
  }

  return schema;
};